import React from "react";
import { useTranslation } from "react-i18next";
import { Identifiable, ProjectReport } from "@planzone/dto";
import {
  useRetrieveProjectReports,
  useAuthentication,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { Window } from "@common/window";
import { Button } from "@common/button";
import { Props } from "./types";
import { ProjectReportSelect } from "./reportSelection";
import { ToggleInput } from "./toggleInput";
import styles from "./styles.scss";

function Form(props: Props) {
  const { t, i18n } = useTranslation();
  const [hasIncludedReport, setHasIncludedReport] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();
  const [formData, setFormData] = React.useState<{
    object: string;
    report: Identifiable<ProjectReport> | null;
  }>({ object: "", report: null });
  const {
    reports,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveProjectReports,
  } = useRetrieveProjectReports(props.project);

  React.useEffect(() => {
    !!props.project.uuid &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectReports(props.project);
  }, [props.project.uuid]);

  function handleDownload() {
    setIsGenerating(true);

    fetch("/pdf/project-synthesis", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${account.token}`,
      },
      body: JSON.stringify({
        projectUuid: props.project.uuid,
        planzoneUuid: planzone.uuid,
        object: formData.object,
        reportUuid: formData.report?.uuid,
        language: i18n.language,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("PDF generation failed");
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${props.project.name}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        props.close();
      })
      .catch(() => {
        setIsGenerating(false);
      });
  }

  return (
    <Window close={props.close}>
      <div className={styles.wrapper}>
        <div className={styles.form}>
          <h1 className={styles.header}>
            {t("projectPage.synthesisPage.pdfExport.form.title")}
          </h1>
          <div className={styles.main}>
            <h2 className={styles.title}>
              {t("projectPage.synthesisPage.pdfExport.form.projectName")}
              {props.project.name}
            </h2>
            <div className={styles.group}>
              <div className={styles.object}>
                <h3 className={styles.label}>
                  {t("projectPage.synthesisPage.pdfExport.form.object")} :
                </h3>
                <p className={styles.limitIndicator}>
                  {formData.object.length} / 500
                </p>
              </div>
              <textarea
                className={styles.input}
                value={formData.object}
                onChange={({ target }) =>
                  setFormData({
                    ...formData,
                    object: target.value.slice(0, 500),
                  })
                }
              />
            </div>
            <div className={styles.row}>
              <h3 className={styles.label}>
                {t("projectPage.synthesisPage.pdfExport.form.addReport")}
              </h3>
              <ToggleInput
                isToggled={hasIncludedReport}
                toggle={() => setHasIncludedReport(!hasIncludedReport)}
              />
            </div>
            {hasIncludedReport &&
              (reports.length > 0 ? (
                <div className={styles.row}>
                  <ProjectReportSelect
                    project={props.project}
                    reports={reports}
                    selectedProjectReport={formData.report || undefined}
                    selectProjectReport={(report) =>
                      setFormData({ ...formData, report: report || null })
                    }
                  />
                </div>
              ) : (
                <p className={styles.reportPlaceholder}>
                  {t("projectPage.synthesisPage.pdfExport.form.noReport")}
                </p>
              ))}
            <div className={styles.buttons}>
              <Button type="cancel" press={props.close}>
                {t("projectPage.synthesisPage.pdfExport.form.cancel")}
              </Button>
              <button
                className={styles.link}
                onClick={handleDownload}
                disabled={isGenerating}
              >
                <p>
                  {isGenerating
                    ? t("projectPage.synthesisPage.pdfExport.form.generating")
                    : t("projectPage.synthesisPage.pdfExport.form.submit")}
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Window>
  );
}

export { Form };
